import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';

import { SousService } from 'app/models/sous-service';
import { ServicesService } from 'app/services/services/services.service';
import { SousServiceService } from 'app/services/sous-Service/sous-service.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-ajouter-sous-service',
  templateUrl: './ajouter-sous-service.component.html',
  styleUrls: ['./ajouter-sous-service.component.scss']
})
export class AjouterSousServiceComponent implements OnInit {
  // Sous-service data
  sousService: SousService = new SousService();
  service: any; // Use 'any' for dynamic properties
  idService: string | null = null;
  agencyId: string | null = null; // For cancel navigation

  // Main image (cover)
  mainImage: string | null = null;
  mainImageFile: File | null = null;
  isUploadingMain: boolean = false;

  // Gallery images (listimages)
  galleryImages: string[] = [];
  selectedGalleryFiles: FileList | null = null;
  isUploadingGallery: boolean = false;
  galleryUploadProgress: number = 0;

  // Form state
  isSubmitting: boolean = false;

  @ViewChild('mainImageInput') mainImageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('galleryFileInput') galleryFileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private serviceService: ServicesService,
    private sousServiceService: SousServiceService,
    private awsService: AwscontrolService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.idService = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadServiceDetails();
    this.serviceService.refreshNedeed.subscribe(() => this.loadServiceDetails());
  }

  loadServiceDetails(): void {
    if (this.idService) {
      this.serviceService.getService(this.idService).subscribe({
        next: (data: any) => {
          this.service = data;
          // Extract agency ID from the service
          if (data.user?.id) {
            this.agencyId = data.user.id.toString();
          } else if (data.userId) {
            this.agencyId = data.userId.toString();
          } else if (data.agencyId) {
            this.agencyId = data.agencyId.toString();
          }
          console.log('Service loaded:', this.service, 'Agency ID:', this.agencyId);
        },
        error: (error) => {
          console.error('Error loading service:', error);
          Swal.fire('Erreur', 'Impossible de charger les détails du service', 'error');
        }
      });
    }
  }

  // ----- TEXT FORMATTING -----
 formatText(command: string): void {
  if (!this.sousService.description) this.sousService.description = '';
  const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
  if (!textarea) return;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = this.sousService.description.substring(start, end);
  let formattedText = '';
  switch (command) {
    case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
    case 'italic': formattedText = `<em>${selectedText}</em>`; break;
    case 'underline': formattedText = `<u>${selectedText}</u>`; break;
    case 'paragraph': formattedText = `<p>${selectedText}</p>`; break;
    case 'list':
      const lines = selectedText.split('\n');
      formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
      break;
    case 'h1': formattedText = `<h1>${selectedText}</h1>`; break;
    case 'h2': formattedText = `<h2>${selectedText}</h2>`; break;
    case 'linebreak':   // ✅ Nouveau cas
      formattedText = '<br>';
      break;
  }
  const newText = this.sousService.description.substring(0, start) + formattedText + this.sousService.description.substring(end);
  this.sousService.description = newText;
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
  }, 0);
}

  // ----- MAIN IMAGE UPLOAD (cover) -----
  triggerMainImageUpload(): void {
    if (!this.isUploadingMain) {
      this.mainImageInput.nativeElement.click();
    }
  }

  onMainImageSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    if (!this.validateImageFile(file)) return;
    this.mainImageFile = file;
    this.uploadMainImage();
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire('Format non supporté', 'Veuillez sélectionner une image (JPEG, PNG, JPG, GIF, WEBP)', 'warning');
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire('Fichier trop volumineux', 'La taille maximale est de 5MB', 'warning');
      return false;
    }
    return true;
  }

  uploadMainImage(): void {
    if (!this.mainImageFile) return;
    this.isUploadingMain = true;
    this.awsService.uploadFile(this.mainImageFile, 'sous-service-main-images').subscribe({
      next: (response) => {
        this.isUploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainImage = response.urls[0];
          Swal.fire({
            icon: 'success',
            title: 'Image principale téléchargée',
            text: 'L\'image de couverture a été ajoutée',
            timer: 1500,
            showConfirmButton: false
          });
          if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
          this.mainImageFile = null;
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploadingMain = false;
        console.error('Main image upload error:', err);
        Swal.fire('Erreur', 'Impossible de télécharger l\'image principale', 'error');
      }
    });
  }

  removeMainImage(): void {
    if (!this.mainImage) return;
    Swal.fire({
      title: 'Supprimer l\'image principale ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, supprimer'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await this.awsService.deleteFile(this.mainImage!).toPromise();
          this.mainImage = null;
          Swal.fire('Supprimée', '', 'success');
        } catch (error) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'image', 'error');
        }
      }
    });
  }

  // ----- GALLERY IMAGES UPLOAD (listimages) -----
  triggerGalleryUpload(): void {
    if (!this.isUploadingGallery) {
      this.galleryFileInput.nativeElement.click();
    }
  }

  onGalleryFilesSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.selectedGalleryFiles = files;
      this.uploadGalleryImages();
      event.target.value = '';
    }
  }

  uploadGalleryImages(): void {
    if (!this.selectedGalleryFiles || this.selectedGalleryFiles.length === 0) return;
    this.isUploadingGallery = true;
    this.galleryUploadProgress = 0;

    Swal.fire({
      title: 'Téléchargement en cours...',
      html: '0%',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    const files = Array.from(this.selectedGalleryFiles);
    const totalFiles = files.length;
    let uploadedCount = 0;
    const validFiles: File[] = [];

    for (const file of files) {
      if (this.validateImageFile(file)) {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) {
      Swal.close();
      Swal.fire('Aucune image valide', '', 'warning');
      this.isUploadingGallery = false;
      return;
    }

    this.awsService.uploadFiles(validFiles, 'sous-service-images').subscribe({
      next: (response) => {
        this.isUploadingGallery = false;
        Swal.close();
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryImages.push(...response.urls);
          Swal.fire({
            icon: 'success',
            title: 'Images téléchargées',
            text: `${response.urls.length} image(s) ajoutée(s) à la galerie`,
            timer: 1500,
            showConfirmButton: false
          });
          if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploadingGallery = false;
        Swal.close();
        console.error('Gallery upload error:', err);
        Swal.fire('Erreur', 'Impossible de télécharger les images de la galerie', 'error');
      }
    });
  }

  deleteGalleryImage(index: number): void {
    const imageUrl = this.galleryImages[index];
    Swal.fire({
      title: 'Supprimer cette image ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, supprimer'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await this.awsService.deleteFile(imageUrl).toPromise();
          this.galleryImages.splice(index, 1);
          Swal.fire('Supprimée', '', 'success');
        } catch (error) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'image', 'error');
        }
      }
    });
  }

  // ----- FORM VALIDATION -----
  validateForm(): boolean {
    if (this.isUploadingMain || this.isUploadingGallery) {
      Swal.fire('Téléchargement en cours', 'Veuillez attendre la fin des téléchargements', 'warning');
      return false;
    }
    if (!this.sousService.name || this.sousService.name.trim() === '') {
      Swal.fire('Nom requis', 'Veuillez saisir un nom', 'warning');
      return false;
    }
    if (!this.sousService.description || this.sousService.description.trim() === '') {
      Swal.fire('Description requise', 'Veuillez saisir une description', 'warning');
      return false;
    }
    if (!this.mainImage) {
      Swal.fire('Image principale requise', 'Veuillez ajouter une image de couverture', 'warning');
      return false;
    }
    return true;
  }

  // ----- SUBMIT -----
  onSubmit(): void {
    if (!this.validateForm()) return;
    if (!this.idService) {
      Swal.fire('Erreur', 'ID service manquant', 'error');
      return;
    }

    this.isSubmitting = true;
    Swal.fire({
      title: 'Création en cours...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    const sousServiceData = {
      name: this.sousService.name.trim(),
      category: this.sousService.category?.trim() || '',
      description: this.sousService.description,
      prix: this.sousService.prix?.trim() || '',
      image_url: this.mainImage,
      listimages: this.galleryImages
    };

    this.sousServiceService.createSousService(sousServiceData, this.idService).subscribe({
      next: () => {
        Swal.close();
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès !',
          text: 'Sous-service créé avec succès',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          this.router.navigate(['/list-Sous-Services', this.idService]);
        });
      },
      error: (error: HttpErrorResponse) => {
        Swal.close();
        this.isSubmitting = false;
        let errorMessage = 'Erreur lors de la création';
        if (error.status === 400) errorMessage = 'Données invalides. Vérifiez les champs.';
        else if (error.status === 401) errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        else if (error.status === 403) errorMessage = 'Permissions insuffisantes.';
        else if (error.status === 500) errorMessage = 'Erreur serveur. Réessayez plus tard.';
        Swal.fire('Erreur', errorMessage, 'error');
      }
    });
  }

  // ----- CANCEL (navigate to /list-Services/:agencyId) -----
  cancel(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-Services', this.agencyId]);
    } else {
      this.router.navigate(['listeServices']);
    }
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser le formulaire ?',
      text: 'Toutes les données non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.sousService = new SousService();
        this.mainImage = null;
        this.galleryImages = [];
        this.mainImageFile = null;
        this.selectedGalleryFiles = null;
        if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
        if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
        Swal.fire('Formulaire réinitialisé', '', 'success');
      }
    });
  }
}